import { useRef, useState } from "react";
import { UploadCloud, X, RefreshCw, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";

const TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp", "application/pdf"];
const MAX = 15 * 1024 * 1024;

function validate(f) {
  if (!TYPES.includes(f.type)) return `${f.name}: only PNG, JPG, JPEG, WEBP or PDF allowed`;
  if (f.size > MAX) return `${f.name}: file is larger than 15MB`;
  return null;
}

// files: [{uid, name, size, content_type, path?, preview?, progress, error?}]
export function FileUploader({ files, setFiles, max = 10, testid = "design-upload" }) {
  const input = useRef(null);
  const [drag, setDrag] = useState(false);

  const upload = async (file, uid) => {
    const fd = new FormData();
    fd.append("files", file);
    try {
      const [ref] = (await http.post("/uploads", fd, {
        onUploadProgress: (e) => setFiles((fs) => fs.map((x) => (x.uid === uid ? { ...x, progress: Math.round((e.loaded / (e.total || 1)) * 100) } : x))),
      })).data;
      setFiles((fs) => fs.map((x) => (x.uid === uid ? { ...x, ...ref, progress: 100 } : x)));
    } catch (e) {
      setFiles((fs) => fs.map((x) => (x.uid === uid ? { ...x, error: errMsg(e) } : x)));
      toast.error(errMsg(e));
    }
  };

  const addFiles = (list, replaceUid) => {
    const arr = Array.from(list);
    for (const f of arr) {
      const err = validate(f);
      if (err) { toast.error(err); continue; }
      const uid = replaceUid || Math.random().toString(36).slice(2);
      const item = { uid, name: f.name, size: f.size, content_type: f.type, progress: 0, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null };
      setFiles((fs) => {
        if (replaceUid) return fs.map((x) => (x.uid === replaceUid ? item : x));
        if (fs.length >= max) { toast.error(`Max ${max} files`); return fs; }
        return [...fs, item];
      });
      upload(f, uid);
      if (replaceUid) break;
    }
  };

  const replace = (uid) => {
    const i = document.createElement("input");
    i.type = "file"; i.accept = TYPES.join(",");
    i.onchange = () => i.files[0] && addFiles(i.files, uid);
    i.click();
  };

  return (
    <div data-testid={testid}>
      <div onClick={() => input.current.click()} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
        data-testid={`${testid}-dropzone`}
        className={`cursor-pointer border-2 border-dashed p-8 text-center transition-colors ${drag ? "border-neon bg-neon/5" : "border-[#333] hover:border-white/60"}`}>
        <UploadCloud className="w-10 h-10 mx-auto text-neon" />
        <p className="mt-3 font-semibold">Drop your artwork here or <span className="text-neon underline">browse</span></p>
        <p className="text-xs text-grey mt-1 font-mono">PNG · JPG · JPEG · WEBP · PDF — max 15MB each, up to {max} files</p>
        <input ref={input} type="file" multiple accept={TYPES.join(",")} className="hidden" data-testid={`${testid}-input`} onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      </div>
      {files.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
          {files.map((f) => (
            <div key={f.uid} className="relative bg-[#171717] border border-[#2c2c2c] p-2" data-testid="uploaded-file-item">
              <div className="aspect-square bg-black grid place-items-center overflow-hidden">
                {f.preview ? <img src={f.preview} alt={f.name} className="w-full h-full object-contain" /> : <FileText className="w-10 h-10 text-grey" />}
              </div>
              <p className="text-[11px] mt-2 truncate">{f.name}</p>
              <p className="text-[10px] font-mono text-grey">{(f.size / 1024 / 1024).toFixed(2)} MB</p>
              {f.error ? <p className="text-[10px] text-red-400 mt-1">Failed — replace or remove</p> : f.path ? <p className="text-[10px] text-neon font-mono mt-1">UPLOADED ✓</p> : (
                <div className="mt-1"><div className="h-1 bg-[#333]"><div className="h-1 bg-neon transition-[width]" style={{ width: `${f.progress}%` }} /></div><p className="text-[10px] font-mono text-grey mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />{f.progress}%</p></div>
              )}
              <div className="absolute top-3 right-3 flex gap-1">
                <button type="button" onClick={() => replace(f.uid)} data-testid="replace-file-button" className="w-7 h-7 bg-black/70 grid place-items-center hover:bg-white hover:text-black" title="Replace"><RefreshCw className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => setFiles((fs) => fs.filter((x) => x.uid !== f.uid))} data-testid="remove-file-button" className="w-7 h-7 bg-black/70 grid place-items-center hover:bg-red-500" title="Remove"><X className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
